import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Cookie, Check, ShieldCheck, BarChart3, Megaphone, Settings2 } from 'lucide-react';
import { Button } from './ui/button';

type CookiePreferences = {
    essential: boolean;
    analytics: boolean;
    marketing: boolean;
};

const CONSENT_KEY = 'coffeeback_cookie_consent';

export function CookieBanner() {
    const { t } = useTranslation();
    const [isVisible, setIsVisible] = useState(false);
    const [showCustomize, setShowCustomize] = useState(false);
    const [preferences, setPreferences] = useState<CookiePreferences>({
        essential: true, // Always true
        analytics: true,
        marketing: true
    });

    useEffect(() => {
        const consent = localStorage.getItem(CONSENT_KEY);
        if (!consent) {
            // Need a slight delay to let the initial animations of the app run
            const timer = setTimeout(() => setIsVisible(true), 1500);
            return () => clearTimeout(timer);
        }

        const handleOpen = () => setIsVisible(true);
        window.addEventListener('open-cookie-banner', handleOpen);
        return () => window.removeEventListener('open-cookie-banner', handleOpen);
    }, []);

    const saveConsent = (prefs: CookiePreferences) => {
        localStorage.setItem(CONSENT_KEY, JSON.stringify(prefs));
        setIsVisible(false);
        // Here you could trigger a global event or context update to init/stop analytics scripts
    };

    const handleAcceptAll = () => {
        saveConsent({ essential: true, analytics: true, marketing: true });
    };

    const handleRejectAll = () => {
        saveConsent({ essential: true, analytics: false, marketing: false });
    };

    const handleSavePreferences = () => {
        saveConsent(preferences);
    };

    const togglePreference = (key: keyof CookiePreferences) => {
        if (key === 'essential') return; // Cannot toggle essential
        setPreferences(prev => ({ ...prev, [key]: !prev[key] }));
    };

    return (
        <AnimatePresence>
            {isVisible && (
                <div className="fixed inset-0 z-[100] md:inset-auto md:bottom-6 md:right-6 md:w-[440px] flex items-end justify-center md:block pointer-events-none p-4 md:p-0">
                    <motion.div
                        layout
                        initial={{ opacity: 0, y: 50, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 50, scale: 0.95 }}
                        transition={{ type: "spring", stiffness: 300, damping: 25 }}
                        className="bg-background/95 backdrop-blur-xl border border-border/50 shadow-2xl rounded-3xl w-full overflow-hidden pointer-events-auto flex flex-col max-h-[90vh]"
                    >
                        {!showCustomize ? (
                            <motion.div
                                key="mainView"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                className="p-6 sm:p-7 flex flex-col gap-5"
                            >
                                <div className="flex items-start gap-4">
                                    <div className="w-12 h-12 shrink-0 rounded-2xl bg-brand-500/10 border border-brand-500/20 text-brand-600 dark:text-brand-400 flex items-center justify-center">
                                        <Cookie size={24} />
                                    </div>
                                    <div className="pt-1">
                                        <h3 className="text-xl font-bold font-display text-foreground leading-tight">
                                            {t('legal.cookieBanner.title')}
                                        </h3>
                                        <p className="text-muted-foreground text-[0.95rem] mt-2 leading-relaxed">
                                            {t('legal.cookieBanner.description')}
                                        </p>
                                    </div>
                                </div>

                                <div className="flex flex-col gap-3 mt-2">
                                    <Button onClick={handleAcceptAll} className="w-full text-[1rem] py-6 rounded-xl font-semibold shadow-md active:scale-[0.98] transition-transform">
                                        {t('legal.cookieBanner.acceptAll')}
                                    </Button>

                                    <div className="flex flex-col sm:flex-row gap-3">
                                        <Button variant="outline" onClick={() => setShowCustomize(true)} className="w-full sm:flex-1 text-foreground/80 hover:text-foreground text-[0.95rem] py-5 rounded-xl border-border/60 hover:bg-secondary/50 whitespace-normal h-auto min-h-[3.5rem] leading-snug">
                                            <Settings2 size={16} className="mr-2 opacity-70 shrink-0" />
                                            {t('legal.cookieBanner.customize')}
                                        </Button>
                                        <Button variant="outline" onClick={handleRejectAll} className="w-full sm:flex-1 text-foreground/80 hover:text-foreground text-[0.95rem] py-5 rounded-xl border-border/60 hover:bg-secondary/50 whitespace-normal h-auto min-h-[3.5rem] leading-snug">
                                            {t('legal.cookieBanner.rejectAll')}
                                        </Button>
                                    </div>
                                </div>

                                <div className="text-center mt-1">
                                    <Link to="/legal/cookies" className="text-xs text-brand-600 dark:text-brand-400 hover:underline font-medium">
                                        {t('legal.cookieBanner.policyLink')}
                                    </Link>
                                </div>
                            </motion.div>
                        ) : (
                            <motion.div
                                key="customizeView"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                className="p-6 sm:p-7 flex flex-col overflow-hidden"
                            >
                                <div className="flex items-center gap-3 mb-6">
                                    <button onClick={() => setShowCustomize(false)} className="text-muted-foreground hover:text-foreground transition-colors shrink-0">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-arrow-left"><path d="m12 19-7-7 7-7"/><path d="M19 12H5"/></svg>
                                    </button>
                                    <h3 className="text-xl font-bold font-display text-foreground leading-tight">
                                        {t('legal.cookieBanner.customize')}
                                    </h3>
                                </div>

                                <div className="flex-1 overflow-y-auto pr-2 pb-4 space-y-6 scrollbar-thin scrollbar-thumb-border/50">
                                    {/* Essential */}
                                    <div className="flex gap-4">
                                        <div className="w-10 h-10 shrink-0 rounded-xl bg-green-500/10 text-green-600 border border-green-500/20 flex items-center justify-center">
                                            <ShieldCheck size={20} />
                                        </div>
                                        <div className="flex-1">
                                            <div className="flex items-start sm:items-center justify-between gap-y-1 gap-x-2 mb-1 flex-wrap">
                                                <h4 className="font-semibold text-foreground text-[0.95rem] pr-2">{t('legal.cookieBanner.categories.essential.title')}</h4>
                                                <div className="px-2 py-0.5 rounded-md bg-secondary text-secondary-foreground text-[0.65rem] font-bold uppercase tracking-wider shrink-0 mt-0.5 sm:mt-0">Sempre Ativo</div>
                                            </div>
                                            <p className="text-muted-foreground text-[0.85rem] leading-relaxed">
                                                {t('legal.cookieBanner.categories.essential.description')}
                                            </p>
                                        </div>
                                    </div>

                                    {/* Analytics */}
                                    <div className="flex gap-4 cursor-pointer group" onClick={() => togglePreference('analytics')}>
                                        <div className={`w-10 h-10 shrink-0 rounded-xl border flex items-center justify-center transition-colors ${preferences.analytics ? 'bg-brand-500/10 border-brand-500/20 text-brand-600 dark:text-brand-400' : 'bg-secondary/50 border-transparent text-muted-foreground'}`}>
                                            <BarChart3 size={20} />
                                        </div>
                                        <div className="flex-1">
                                            <div className="flex items-center justify-between mb-1">
                                                <h4 className="font-semibold text-foreground text-[0.95rem]">{t('legal.cookieBanner.categories.analytics.title')}</h4>
                                                <Switch checked={preferences.analytics} onChange={() => togglePreference('analytics')} />
                                            </div>
                                            <p className="text-muted-foreground text-[0.85rem] leading-relaxed">
                                                {t('legal.cookieBanner.categories.analytics.description')}
                                            </p>
                                        </div>
                                    </div>

                                    {/* Marketing */}
                                    <div className="flex gap-4 cursor-pointer group" onClick={() => togglePreference('marketing')}>
                                        <div className={`w-10 h-10 shrink-0 rounded-xl border flex items-center justify-center transition-colors ${preferences.marketing ? 'bg-brand-500/10 border-brand-500/20 text-brand-600 dark:text-brand-400' : 'bg-secondary/50 border-transparent text-muted-foreground'}`}>
                                            <Megaphone size={20} />
                                        </div>
                                        <div className="flex-1">
                                            <div className="flex items-center justify-between mb-1">
                                                <h4 className="font-semibold text-foreground text-[0.95rem]">{t('legal.cookieBanner.categories.marketing.title')}</h4>
                                                <Switch checked={preferences.marketing} onChange={() => togglePreference('marketing')} />
                                            </div>
                                            <p className="text-muted-foreground text-[0.85rem] leading-relaxed">
                                                {t('legal.cookieBanner.categories.marketing.description')}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-4 pt-4 border-t border-border/50">
                                    <Button onClick={handleSavePreferences} className="w-full text-[1rem] py-6 rounded-xl font-semibold shadow-md active:scale-[0.98] transition-transform">
                                        <Check size={18} className="mr-2" />
                                        {t('legal.cookieBanner.savePreferences')}
                                    </Button>
                                    <div className="text-center mt-4">
                                        <Link to="/legal/cookies" className="text-xs text-muted-foreground hover:text-foreground transition-colors font-medium">
                                            {t('legal.cookieBanner.policyLink')}
                                        </Link>
                                    </div>
                                </div>
                            </motion.div>
                        )}
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
}

// Custom Switch purely for use in CookieBanner, mimics iOS/shadcn style
function Switch({ checked, onChange }: { checked: boolean; onChange: () => void }) {
    return (
        <button
            type="button"
            role="switch"
            aria-checked={checked}
            onClick={(e) => {
                e.stopPropagation();
                onChange();
            }}
            className={`
                relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 focus-visible:ring-offset-background
                ${checked ? 'bg-primary' : 'bg-neutral-300 dark:bg-neutral-600'}
            `}
        >
            <span
                className={`
                    pointer-events-none block h-5 w-5 rounded-full bg-background shadow-lg ring-0 transition-transform
                    ${checked ? 'translate-x-5' : 'translate-x-0'}
                `}
            />
        </button>
    );
}
